/**
 * EarlyBird Quiz v2 Module
 * Version: 2.0.0
 * 
 * Features:
 * - 14 questions + email capture
 * - 3 reassurance panels
 * - 2 commitment gates during loading
 * - Dynamic results with personalized pattern diagnosis
 * - Constraint-specific messaging
 * - Full GA4 + Meta Pixel tracking
 */

(function() {
  'use strict';

  // ==========================================
  // QUIZ ANALYTICS MODULE
  // ==========================================
  const QuizAnalytics = {
    quizName: 'earlybird_morning_energy_type_v2',
    enableGA4: true,
    enableMeta: true,
    sessionId: null,

    init(config) {
      this.quizName = config.quizName || this.quizName;
      this.enableGA4 = config.enableGA4 !== false;
      this.enableMeta = config.enableMeta !== false;
      this.sessionId = this.getOrCreateSessionId();
    },

    getOrCreateSessionId() {
      let sessionId = sessionStorage.getItem('eb_quiz_session');
      if (!sessionId) {
        sessionId = 'ebq2_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        sessionStorage.setItem('eb_quiz_session', sessionId);
      }
      return sessionId;
    },

    getTrafficSource() {
      const params = new URLSearchParams(window.location.search);
      return {
        source: params.get('utm_source') || 'direct',
        medium: params.get('utm_medium') || 'none',
        campaign: params.get('utm_campaign') || 'none',
        content: params.get('utm_content') || '',
        term: params.get('utm_term') || ''
      };
    },

    track(eventName, eventData) {
      const payload = {
        quiz_name: this.quizName,
        quiz_version: '2.0',
        session_id: this.sessionId,
        ...eventData
      };

      if (this.enableGA4 && typeof gtag === 'function') {
        gtag('event', eventName, payload);
      }

      if (this.enableMeta && typeof fbq === 'function') {
        fbq('trackCustom', eventName, payload);
      }

      if (window.location.hostname === 'localhost' || window.location.search.includes('debug=true')) {
        console.log('[EarlyBird Quiz v2]', eventName, payload);
      }
    },

    trackLandingView() {
      const traffic = this.getTrafficSource();
      this.track('quiz_landing_view', {
        traffic_source: traffic.source,
        traffic_medium: traffic.medium,
        traffic_campaign: traffic.campaign,
        page_url: window.location.pathname
      });
    },

    trackQuizStart() {
      const traffic = this.getTrafficSource();
      this.track('quiz_start', {
        traffic_source: traffic.source,
        traffic_medium: traffic.medium,
        traffic_campaign: traffic.campaign
      });
    },

    trackQuestionView(questionNumber, questionId, questionType) {
      this.track('quiz_question_view', {
        question_number: questionNumber,
        question_id: questionId,
        question_type: questionType
      });
    },

    trackQuestionAnswer(questionNumber, questionId, answer, timeOnQuestion) {
      const sanitizedAnswer = Array.isArray(answer) 
        ? answer.join(' | ').substring(0, 300)
        : String(answer).substring(0, 300);

      this.track('quiz_question_answer', {
        question_number: questionNumber,
        question_id: questionId,
        answer_value: sanitizedAnswer,
        time_on_question_seconds: Math.round(timeOnQuestion / 1000)
      });
    },

    trackPanelView(panelId, panelType) {
      this.track('quiz_panel_view', {
        panel_id: panelId,
        panel_type: panelType
      });
    },

    trackSegment(segmentKey, segmentValue) {
      this.track('quiz_segment', {
        segment_key: segmentKey,
        segment_value: segmentValue.substring(0, 100)
      });

      if (this.enableMeta && typeof fbq === 'function') {
        fbq('trackCustom', 'QuizSegment_' + segmentKey, { value: segmentValue.substring(0, 100) });
      }
    },

    trackEmailCapture(hasMarketingConsent) {
      this.track('quiz_email_capture', {
        marketing_consent: hasMarketingConsent
      });

      if (this.enableMeta && typeof fbq === 'function') {
        fbq('track', 'Lead', { content_name: this.quizName });
      }
    },

    trackCommitmentGate(gateNumber, response) {
      this.track('quiz_commitment_gate', {
        gate_number: gateNumber,
        response: response
      });
    },

    trackQuizComplete(answers, patternName, totalTime) {
      this.track('quiz_complete', {
        pattern_name: patternName,
        motivation_level: answers.motivation_level || '',
        obstacle: answers.biggest_obstacle || '',
        aspiration: answers.aspirational_identity || '',
        total_questions: Object.keys(answers).length,
        total_time_seconds: Math.round(totalTime / 1000)
      });

      if (this.enableMeta && typeof fbq === 'function') {
        fbq('track', 'CompleteRegistration', {
          content_name: this.quizName,
          status: 'complete'
        });
      }

      this.storeForAttribution(answers, patternName);
    },

    trackQuizAbandon(lastQuestionNumber, lastQuestionId, totalTime) {
      this.track('quiz_abandon', {
        last_question_number: lastQuestionNumber,
        last_question_id: lastQuestionId,
        questions_completed: lastQuestionNumber - 1,
        total_time_seconds: Math.round(totalTime / 1000)
      });
    },

    trackCTAClick(ctaType, patternName) {
      this.track('quiz_cta_click', {
        cta_type: ctaType,
        pattern_name: patternName
      });

      if (this.enableMeta && typeof fbq === 'function') {
        fbq('track', 'AddToCart', {
          content_name: 'EarlyBird Morning Cocktail',
          content_category: 'Quiz Result',
          value: 38.08,
          currency: 'USD'
        });
      }
    },

    storeForAttribution(answers, patternName) {
      const quizData = {
        sessionId: this.sessionId,
        pattern: patternName,
        obstacle: answers.biggest_obstacle,
        motivation: answers.motivation_level,
        aspiration: answers.aspirational_identity,
        email: answers.email_capture,
        completedAt: new Date().toISOString()
      };
      
      localStorage.setItem('eb_quiz_data_v2', JSON.stringify(quizData));
      document.cookie = `eb_quiz_pattern=${encodeURIComponent(patternName)}; path=/; max-age=2592000`;
    }
  };


  // ==========================================
  // QUIZ UI MODULE
  // ==========================================
  class EarlyBirdQuizV2 {
    constructor(container) {
      this.container = container;
      this.config = this.loadConfig();
      this.currentStep = 0;
      this.answers = {};
      this.questionStartTime = Date.now();
      this.quizStartTime = null;
      this.completed = false;
      this.emailCaptured = false;
      this.commitmentResponses = {};

      this.elements = {
        landingScreen: container.querySelector('[data-landing-screen]'),
        startBtn: container.querySelector('[data-start-quiz]'),
        quizContainer: container.querySelector('[data-quiz-container]'),
        questionContainer: container.querySelector('[data-question-content]'),
        progressFill: container.querySelector('[data-progress-fill]'),
        currentStepEl: container.querySelector('[data-current-step]'),
        totalStepsEl: container.querySelector('[data-total-steps]'),
        backBtn: container.querySelector('[data-action="back"]'),
        continueBtn: container.querySelector('[data-continue-btn]'),
        loadingScreen: container.querySelector('[data-loading-screen]'),
        loadingMain: container.querySelector('[data-loading-main]'),
        loadingProgress: container.querySelector('[data-loading-progress]'),
        loadingStatus: container.querySelector('[data-loading-status]'),
        commitment1: container.querySelector('[data-commitment-1]'),
        commitment2: container.querySelector('[data-commitment-2]'),
        resultsScreen: container.querySelector('[data-results-screen]')
      };

      this.init();
    }

    loadConfig() {
      const configEl = this.container.querySelector('[data-quiz-config]') || document.querySelector('[data-quiz-config]');
      if (configEl) {
        try {
          return JSON.parse(configEl.textContent);
        } catch (e) {
          console.error('Failed to parse quiz config:', e);
        }
      }
      return { questions: [], patterns: {}, obstacleResponses: {}, testimonials: {}, offer: {} };
    }

    init() {
      QuizAnalytics.init({
        quizName: this.container.dataset.quizName,
        enableGA4: this.container.dataset.enableGa4 !== 'false',
        enableMeta: this.container.dataset.enableMeta !== 'false'
      });

      // Count actual questions (excluding panels)
      this.questionCount = this.config.questions.filter(q => q.type !== 'panel').length;
      this.elements.totalStepsEl.textContent = this.questionCount;

      this.bindEvents();
      QuizAnalytics.trackLandingView();
      window.addEventListener('beforeunload', () => this.handleAbandon());
    }

    bindEvents() {
      this.elements.startBtn.addEventListener('click', () => this.startQuiz());
      this.elements.backBtn.addEventListener('click', () => this.goBack());
      this.elements.continueBtn.addEventListener('click', () => this.goNext());

      // Commitment gate buttons
      this.container.querySelectorAll('[data-commitment-answer]').forEach(btn => {
        btn.addEventListener('click', (e) => this.handleCommitmentAnswer(e));
      });
    }

    startQuiz() {
      this.quizStartTime = Date.now();
      this.elements.landingScreen.style.display = 'none';
      this.elements.quizContainer.style.display = 'block';
      QuizAnalytics.trackQuizStart();
      this.renderQuestion();
    }

    getVisualStepNumber() {
      // Count only non-panel questions up to current step
      let count = 0;
      for (let i = 0; i <= this.currentStep; i++) {
        if (this.config.questions[i].type !== 'panel') {
          count++;
        }
      }
      return count;
    }

    renderQuestion() {
      const question = this.config.questions[this.currentStep];
      if (!question) return;

      this.updateProgress();
      this.questionStartTime = Date.now();

      // Track view
      if (question.type === 'panel') {
        QuizAnalytics.trackPanelView(question.id, question.panelType);
      } else {
        QuizAnalytics.trackQuestionView(this.getVisualStepNumber(), question.id, question.type);
      }

      // Show/hide back button
      this.elements.backBtn.style.visibility = this.currentStep > 0 ? 'visible' : 'hidden';

      // Render based on type
      let html = '';
      switch (question.type) {
        case 'single': html = this.renderSingleSelect(question); break;
        case 'multi': html = this.renderMultiSelect(question); break;
        case 'panel': html = this.renderPanel(question); break;
        case 'email': html = this.renderEmailCapture(question); break;
      }

      this.elements.questionContainer.innerHTML = html;
      this.animateIn();
      this.bindOptionEvents(question);
      this.updateContinueButton(question);
    }

    renderSingleSelect(question) {
      return `
        <h2 class="eb-quiz__question-title">${this.escapeHtml(question.question)}</h2>
        ${question.subtitle ? `<p class="eb-quiz__question-subtitle">${this.escapeHtml(question.subtitle)}</p>` : ''}
        <div class="eb-quiz__options">
          ${question.options.map((option, i) => `
            <button class="eb-quiz__option" data-option-index="${i}" data-option-value="${this.escapeHtml(option)}">
              ${this.escapeHtml(option)}
            </button>
          `).join('')}
        </div>
      `;
    }

    renderMultiSelect(question) {
      const selected = this.answers[question.id] || [];
      return `
        <h2 class="eb-quiz__question-title">${this.escapeHtml(question.question)}</h2>
        ${question.subtitle ? `<p class="eb-quiz__question-subtitle">${this.escapeHtml(question.subtitle)}</p>` : ''}
        ${question.instruction ? `<p class="eb-quiz__question-instruction">${this.escapeHtml(question.instruction)}</p>` : ''}
        <div class="eb-quiz__options">
          ${question.options.map((option, i) => `
            <button class="eb-quiz__option ${selected.includes(option) ? 'eb-quiz__option--selected' : ''}" 
                    data-option-index="${i}" data-option-value="${this.escapeHtml(option)}" data-multi="true">
              <span class="eb-quiz__checkbox"><span class="eb-quiz__checkbox-icon">✓</span></span>
              ${this.escapeHtml(option)}
            </button>
          `).join('')}
        </div>
      `;
    }

    renderPanel(question) {
      switch (question.panelType) {
        case 'normalization':
        case 'mechanism':
          return this.renderEducationPanel(question);
        case 'testimonials':
          return this.renderTestimonialsPanel(question);
        default:
          return this.renderEducationPanel(question);
      }
    }

    renderEducationPanel(question) {
      let bulletHtml = '';
      if (question.bullets && question.bullets.length) {
        bulletHtml = `
          <ul class="eb-quiz__mechanism-bullets">
            ${question.bullets.map(b => `<li>${this.escapeHtml(b)}</li>`).join('')}
          </ul>
        `;
      }

      let conclusionHtml = '';
      if (question.conclusion) {
        conclusionHtml = `<div class="eb-quiz__mechanism-conclusion">${question.conclusion}</div>`;
      }

      const isNormalization = question.panelType === 'normalization';

      if (isNormalization) {
        return `
          <div class="eb-quiz__panel">
            <h2 class="eb-quiz__panel-title">${question.title}</h2>
            <div class="eb-quiz__panel-content">${question.content}</div>
            ${question.cta ? `<p class="eb-quiz__panel-cta">${question.cta}</p>` : ''}
            <button class="eb-quiz__panel-btn" data-panel-continue>${question.button || 'CONTINUE'}</button>
          </div>
        `;
      }

      // Mechanism panel with dark card
      return `
        <div class="eb-quiz__panel">
          <h2 class="eb-quiz__panel-title">${question.title}</h2>
          <div class="eb-quiz__mechanism-card">
            <div>${question.content}</div>
            ${bulletHtml}
            ${conclusionHtml}
          </div>
          <button class="eb-quiz__panel-btn" data-panel-continue>${question.button || 'CONTINUE'}</button>
        </div>
      `;
    }

    renderTestimonialsPanel(question) {
      return `
        <div class="eb-quiz__panel">
          <h2 class="eb-quiz__panel-title">${question.title}</h2>
          <div class="eb-quiz__panel-testimonials">
            ${question.testimonials.map(t => `
              <div class="eb-quiz__panel-testimonial">
                <p class="eb-quiz__panel-testimonial-quote">"${this.escapeHtml(t.quote)}"</p>
                <p class="eb-quiz__panel-testimonial-author">
                  — ${this.escapeHtml(t.author)}${t.detail ? `, <span class="eb-quiz__panel-testimonial-detail">${this.escapeHtml(t.detail)}</span>` : ''}
                </p>
              </div>
            `).join('')}
          </div>
          ${question.cta ? `<p class="eb-quiz__panel-cta">${question.cta}</p>` : ''}
          <button class="eb-quiz__panel-btn" data-panel-continue>${question.button || 'CONTINUE'}</button>
        </div>
      `;
    }

    renderEmailCapture(question) {
    return `
      <div class="eb-quiz__panel">
        <h2 class="eb-quiz__question-title">${question.question}</h2>
        <p class="eb-quiz__question-subtitle">${question.subtitle}</p>
        <div class="eb-quiz__email-form">
          <input type="email" class="eb-quiz__email-input" placeholder="${question.placeholder}" data-email-input required>
          <p class="eb-quiz__email-consent">${question.consentText || ''}</p>
          <button class="eb-quiz__email-btn" data-email-submit>${question.button}</button>
          <p class="eb-quiz__email-privacy">${question.privacyText}</p>
        </div>
      </div>
    `;
    }

    bindOptionEvents(question) {
      if (question.type === 'single') {
        this.elements.questionContainer.querySelectorAll('.eb-quiz__option').forEach(btn => {
          btn.addEventListener('click', () => this.selectOption(question, btn.dataset.optionValue));
        });
      }

      if (question.type === 'multi') {
        this.elements.questionContainer.querySelectorAll('.eb-quiz__option').forEach(btn => {
          btn.addEventListener('click', () => this.toggleMultiOption(question, btn));
        });
      }

      if (question.type === 'panel') {
        const panelBtn = this.elements.questionContainer.querySelector('[data-panel-continue]');
        if (panelBtn) {
          panelBtn.addEventListener('click', () => this.goNext());
        }
      }

      if (question.type === 'email') {
        const emailBtn = this.elements.questionContainer.querySelector('[data-email-submit]');
        const emailInput = this.elements.questionContainer.querySelector('[data-email-input]');
        
        if (emailBtn && emailInput) {
          emailBtn.addEventListener('click', () => this.handleEmailSubmit());
          emailInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') this.handleEmailSubmit();
          });
        }
      }
    }

    selectOption(question, value) {
      this.elements.questionContainer.querySelectorAll('.eb-quiz__option').forEach(btn => {
        btn.classList.toggle('eb-quiz__option--selected', btn.dataset.optionValue === value);
      });

      this.answers[question.id] = value;
      
      const timeOnQuestion = Date.now() - this.questionStartTime;
      QuizAnalytics.trackQuestionAnswer(this.getVisualStepNumber(), question.id, value, timeOnQuestion);

      if (question.segmentKey) {
        QuizAnalytics.trackSegment(question.segmentKey, value);
      }

      setTimeout(() => this.goNext(), 300);
    }

    toggleMultiOption(question, btn) {
      const value = btn.dataset.optionValue;
      let selected = this.answers[question.id] || [];

      if (selected.includes(value)) {
        selected = selected.filter(v => v !== value);
        btn.classList.remove('eb-quiz__option--selected');
      } else {
        selected.push(value);
        btn.classList.add('eb-quiz__option--selected');
      }

      this.answers[question.id] = selected;
      this.updateContinueButton(question);
    }

    handleEmailSubmit() {
  const emailInput = this.elements.questionContainer.querySelector('[data-email-input]');
  this.answers.marketing_consent = true;
  const email = emailInput?.value?.trim();

  if (!email || !this.isValidEmail(email)) {
    emailInput.style.borderColor = '#EF4444';
    emailInput.focus();
    return;
  }

  this.answers.email_capture = email;
  this.answers.marketing_consent = true;
  this.emailCaptured = true;

  QuizAnalytics.trackEmailCapture(this.answers.marketing_consent);

  const question = this.config.questions[this.currentStep];
  const timeOnQuestion = Date.now() - this.questionStartTime;
  QuizAnalytics.trackQuestionAnswer(this.getVisualStepNumber(), question.id, '[email]', timeOnQuestion);

  // Send to Klaviyo
  this.sendToKlaviyo(email, this.answers.marketing_consent);

  this.showLoading();
}

sendToKlaviyo(email, marketingConsent) {
  const patternName = this.determinePattern();
  const properties = {
    'Morning Energy Pattern': patternName,
    'Quiz Age Range': this.answers.age || '',
    'Quiz Gender': this.answers.gender || '',
    'Quiz Struggle Duration': this.answers.struggle_duration || '',
    'Quiz Biggest Obstacle': this.answers.biggest_obstacle || '',
    'Quiz Motivation Level': this.answers.motivation_level || '',
    'Quiz Aspiration': this.answers.aspirational_identity || '',
    'Quiz Morning Symptoms': (this.answers.morning_symptoms || []).join(', '),
    'Quiz Current Routine': (this.answers.current_routine || []).join(', '),
    'Quiz Marketing Consent': marketingConsent,
    'Quiz Completed Date': new Date().toISOString()
  };

  // Use Klaviyo's onsite JS (already loaded on the store)
  if (typeof klaviyo !== 'undefined') {
    klaviyo.push(['identify', { email: email, ...properties }]);
    klaviyo.push(['track', 'Quiz Completed', properties]);
  }

  // Also subscribe to list via Klaviyo Forms API
  const KLAVIYO_LIST_ID = 'Xwcyv3';
  const companyId = 'TaWKDA';

  fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${companyId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
    body: JSON.stringify({
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Morning Energy Quiz v2',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                properties: properties
              }
            }
          }
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: KLAVIYO_LIST_ID
            }
          }
        }
      }
    })
  }).catch(err => console.error('Klaviyo subscription error:', err));
}

    isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }

    updateContinueButton(question) {
      if (question.type === 'multi') {
        const selected = this.answers[question.id] || [];
        const minSelections = question.minSelections || 1;
        this.elements.continueBtn.style.display = 'block';
        const hasEnough = selected.length >= minSelections;
        this.elements.continueBtn.classList.toggle('eb-quiz__continue-btn--active', hasEnough);
        this.elements.continueBtn.disabled = !hasEnough;
      } else {
        this.elements.continueBtn.style.display = 'none';
      }
    }

    updateProgress() {
      const visualStep = this.getVisualStepNumber();
      const progress = (visualStep / this.questionCount) * 100;
      this.elements.progressFill.style.width = `${progress}%`;
      this.elements.currentStepEl.textContent = visualStep;
    }

    animateIn() {
      this.elements.questionContainer.parentElement.style.animation = 'none';
      this.elements.questionContainer.parentElement.offsetHeight;
      this.elements.questionContainer.parentElement.style.animation = 'eb-fadeIn 0.4s ease';
    }

    goNext() {
      const question = this.config.questions[this.currentStep];
      
      // Track multi-select answer
      if (question.type === 'multi') {
        const timeOnQuestion = Date.now() - this.questionStartTime;
        QuizAnalytics.trackQuestionAnswer(
          this.getVisualStepNumber(), 
          question.id, 
          this.answers[question.id] || [], 
          timeOnQuestion
        );
      }

      if (this.currentStep < this.config.questions.length - 1) {
        this.currentStep++;
        this.renderQuestion();
      } else if (question.type !== 'email') {
        // Should not reach here normally since email triggers loading
        this.showLoading();
      }
    }

    goBack() {
      if (this.currentStep > 0) {
        this.currentStep--;
        this.renderQuestion();
      }
    }

    handleAbandon() {
      if (!this.completed && this.quizStartTime && this.currentStep > 0) {
        const question = this.config.questions[this.currentStep];
        const totalTime = Date.now() - this.quizStartTime;
        QuizAnalytics.trackQuizAbandon(this.getVisualStepNumber(), question?.id, totalTime);
      }
    }

    showLoading() {
      this.elements.quizContainer.style.display = 'none';
      this.elements.loadingScreen.style.display = 'flex';
      this.elements.loadingMain.style.display = 'block';
      this.elements.commitment1.style.display = 'none';
      this.elements.commitment2.style.display = 'none';

      let progress = 0;
      const checkmarks = ['symptoms', 'causes', 'recommendations'];
      let currentCheck = 0;

      const progressInterval = setInterval(() => {
        progress += 1.5;
        this.elements.loadingProgress.style.width = `${Math.min(progress, 100)}%`;

        // Update checkmarks
        if (progress >= 30 && currentCheck === 0) {
          this.completeCheck('symptoms');
          currentCheck = 1;
        }
        if (progress >= 60 && currentCheck === 1) {
          this.completeCheck('causes');
          currentCheck = 2;
        }
        if (progress >= 90 && currentCheck === 2) {
          this.completeCheck('recommendations');
          currentCheck = 3;
        }

        // Show commitment gates
        if (progress >= 45 && progress < 47 && !this.commitmentResponses[1]) {
          clearInterval(progressInterval);
          this.showCommitmentGate(1, progressInterval);
          return;
        }

        if (progress >= 75 && progress < 77 && !this.commitmentResponses[2]) {
          clearInterval(progressInterval);
          this.showCommitmentGate(2, progressInterval);
          return;
        }

        if (progress >= 100) {
          clearInterval(progressInterval);
          setTimeout(() => this.showResults(), 500);
        }
      }, 80);
    }

    completeCheck(checkId) {
      const check = this.container.querySelector(`[data-check="${checkId}"]`);
      if (check) {
        check.classList.add('eb-quiz__loading-check--complete');
        check.querySelector('.eb-quiz__loading-check-icon').textContent = '✓';
      }
    }

    showCommitmentGate(gateNumber, pausedInterval) {
      this.elements.loadingMain.style.display = 'none';
      const gate = gateNumber === 1 ? this.elements.commitment1 : this.elements.commitment2;
      gate.style.display = 'block';

      // Store the interval to resume later
      gate.dataset.pausedProgress = this.elements.loadingProgress.style.width;
    }

    handleCommitmentAnswer(e) {
      const btn = e.target;
      const response = btn.dataset.commitmentAnswer;
      const gate = btn.closest('.eb-quiz__commitment');
      const gateNumber = gate === this.elements.commitment1 ? 1 : 2;

      this.commitmentResponses[gateNumber] = response;
      QuizAnalytics.trackCommitmentGate(gateNumber, response);

      // Hide gate, show loading again
      gate.style.display = 'none';
      this.elements.loadingMain.style.display = 'block';

      // Resume progress
      let currentProgress = parseFloat(this.elements.loadingProgress.style.width) || 0;
      
      const resumeInterval = setInterval(() => {
        currentProgress += 1.5;
        this.elements.loadingProgress.style.width = `${Math.min(currentProgress, 100)}%`;

        if (currentProgress >= 60 && !this.container.querySelector('[data-check="causes"]').classList.contains('eb-quiz__loading-check--complete')) {
          this.completeCheck('causes');
        }
        if (currentProgress >= 90 && !this.container.querySelector('[data-check="recommendations"]').classList.contains('eb-quiz__loading-check--complete')) {
          this.completeCheck('recommendations');
        }

        // Check for gate 2
        if (currentProgress >= 75 && currentProgress < 77 && gateNumber === 1 && !this.commitmentResponses[2]) {
          clearInterval(resumeInterval);
          this.showCommitmentGate(2, resumeInterval);
          return;
        }

        if (currentProgress >= 100) {
          clearInterval(resumeInterval);
          setTimeout(() => this.showResults(), 500);
        }
      }, 80);
    }

    showResults() {
      this.completed = true;
      this.elements.loadingScreen.style.display = 'none';
      this.elements.resultsScreen.style.display = 'block';

      const patternName = this.determinePattern();
      const totalTime = Date.now() - this.quizStartTime;
      
      QuizAnalytics.trackQuizComplete(this.answers, patternName, totalTime);

      this.elements.resultsScreen.innerHTML = this.renderResultsPage(patternName);
      this.bindResultsEvents(patternName);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    determinePattern() {
      const coffeeHeavy = this.answers.current_routine?.includes('Coffee (3+ cups)') || 
                          this.answers.current_routine?.includes('Energy drinks (Red Bull, Monster, Bang, etc.)');
      const hasCrash = this.answers.coffee_crash?.includes('crash') || this.answers.coffee_crash?.includes('Jittery');
      const wiredTired = this.answers.day_effects?.includes('Feeling "wired but tired"');
      const longStruggle = this.answers.struggle_duration === 'As long as I can remember' || 
                          this.answers.struggle_duration === '3-5 years';
      const fogFatigue = this.answers.morning_symptoms?.includes('Brain fog') || 
                         this.answers.wake_time?.includes('foggy');

      if (coffeeHeavy && hasCrash) {
        return 'Cortisol Overload Pattern';
      } else if (wiredTired && longStruggle) {
        return 'Adrenal Fatigue Pattern';
      } else {
        return 'Chronic Dehydration Pattern';
      }
    }

    renderResultsPage(patternName) {
      const symptoms = this.answers.morning_symptoms || [];
      const dayEffects = this.answers.day_effects || [];
      const allSymptoms = [...symptoms, ...dayEffects].slice(0, 5);
      
      const obstacle = this.answers.biggest_obstacle || '';
      const obstacleResponse = this.config.obstacleResponses[obstacle] || {
        title: 'Now You Know What\'s Really Going On',
        content: 'Understanding the cortisol-crash cycle is the first step to breaking it.'
      };

      const testimonials = this.config.testimonials.default || [];
      const offer = this.config.offer || {};

      const coffeeAnswer = this.answers.current_routine || [];
      let behaviorText = 'rely on coffee to get going';
      if (coffeeAnswer.includes('Coffee (3+ cups)')) {
        behaviorText = 'drink 3+ cups of coffee';
      } else if (coffeeAnswer.includes('Energy drinks (Red Bull, Monster, Bang, etc.)')) {
        behaviorText = 'rely on energy drinks';
      }

      return `
        <div class="eb-quiz__results-header">
          <p class="eb-quiz__results-label">Your Morning Energy Assessment</p>
          <h1 class="eb-quiz__results-pattern">${patternName}</h1>
          <p class="eb-quiz__results-description">Based on your responses, we've identified what's likely happening — and what you can do about it.</p>
        </div>

        <div class="eb-quiz__results-content">
          
          <!-- Diagnosis Card -->
          <div class="eb-quiz__results-card">
            <h2 class="eb-quiz__results-card-title">Your Pattern Explained</h2>
            <p>Based on your responses, you're experiencing what researchers call <strong>${patternName}</strong>.</p>
            <p>This pattern is characterized by:</p>
            <div class="eb-quiz__results-symptoms">
              ${allSymptoms.map(s => `<span class="eb-quiz__results-symptom">${this.escapeHtml(s)}</span>`).join('')}
            </div>
            <p><strong>The important thing to understand:</strong> This isn't a discipline problem. It's a biochemical pattern that's actually quite common — and addressable.</p>
          </div>

          <!-- Why This Happens -->
          <div class="eb-quiz__results-card">
            <h2 class="eb-quiz__results-card-title">Why You Feel This Way</h2>
            <img src="https://cdn.shopify.com/s/files/1/0043/9382/8423/files/why-you-feel-this-way.jpg?v=1771619422" alt="" />
            <p>When you ${behaviorText}, you're triggering a cortisol spike on top of your body's natural morning cortisol.</p>
            <div class="eb-quiz__results-mechanism">
              <p>This creates a "stress stack" that leads to:</p>
              <ul class="eb-quiz__results-mechanism-list">
                <li>The jittery-but-tired feeling</li>
                <li>The 2-3pm crash you've been experiencing</li>
                <li>The need for MORE caffeine just to feel normal</li>
              </ul>
            </div>
            <p>Over time, this pattern exhausts your adrenal glands and makes mornings progressively harder.</p>
            <p><strong>You're not lazy. You're not undisciplined. You've been working against your own biochemistry.</strong></p>
          </div>

          <!-- Constraint Removal -->
          <div class="eb-quiz__results-constraint">
            <h3 class="eb-quiz__results-constraint-title">${obstacleResponse.title}</h3>
            <p>${obstacleResponse.content}</p>
          </div>

          <!-- Solution -->
          <div class="eb-quiz__results-solution">
            <h2 class="eb-quiz__results-solution-title">What 250,000+ People With Your Pattern Are Using</h2>
            <img src="https://cdn.shopify.com/s/files/1/0043/9382/8423/files/collage2.jpg?v=1771618289" alt="" />
            <p>The EarlyBird Morning Cocktail was specifically designed to address the cortisol-crash cycle.</p><br>
            <p>Instead of spiking your stress hormones like coffee does, it works by:</p>
            <ul class="eb-quiz__results-benefits">
              <li><strong>Providing clean, buffered caffeine</strong> (natural green coffee bean + L-Theanine) that doesn't trigger cortisol overload</li>
              <li><strong>Replenishing hydration</strong> you lost overnight (you lose over a pound of water while sleeping)</li>
              <li><strong>Supporting mood and motivation</strong> with nootropics and KSM-66 Ashwagandha that reduce stress response</li>
            </ul>
            <div class="eb-quiz__results-ritual">
              <strong>The Ritual:</strong> Mix it the night before, put it on your nightstand, and drink it the moment your alarm goes off. Within 5-15 minutes, your brain switches ON. No fog. No negotiation. Just... clarity.
            </div>
            <a href="https://clubearlybird.com/pages/new-dynamic-golife"><img src="https://cdn.shopify.com/s/files/1/0043/9382/8423/files/not-just-caffeine---brain-fuel.png?v=1771186493" alt="" /></a>
          </div>

          <!-- Testimonials -->
          <div class="eb-quiz__results-testimonials">
            <h3 class="eb-quiz__results-testimonials-title">Here's What People With Your Pattern Are Saying:</h3>
            ${testimonials.map(t => `
              <div class="eb-quiz__results-testimonial">
                <p class="eb-quiz__results-testimonial-quote">"${this.escapeHtml(t.quote)}"</p>
                <p class="eb-quiz__results-testimonial-author">— ${this.escapeHtml(t.author)}</p>
              </div>
            `).join('')}
          </div>

          <!-- Offer -->
          <div class="eb-quiz__results-offer">
            <h2 class="eb-quiz__results-offer-headline">${offer.headline || 'Ready To Break The Cycle?'}</h2>
            <img src="https://cdn.shopify.com/s/files/1/0043/9382/8423/files/quiz-offer.jpg?v=1771620076" alt="" />
            <p class="eb-quiz__results-offer-subheadline">${offer.subheadline || 'For a limited time, get 44% off your first order + free bonuses'}</p>
            
            <div class="eb-quiz__results-offer-items">
              ${(offer.items || []).map(item => `
                <div class="eb-quiz__results-offer-item">
                  <span class="eb-quiz__results-offer-item-name">${item.name}</span>
                  <span class="eb-quiz__results-offer-item-value">${item.value}</span>
                </div>
              `).join('')}
            </div>
            
            <div class="eb-quiz__results-offer-total">
              <span class="eb-quiz__results-offer-total-label">Your Price Today:</span>
              <span class="eb-quiz__results-offer-total-price">${offer.price || '$38.08'}</span>
            </div>
            
            <button class="eb-quiz__results-offer-cta" data-cta-main>${offer.cta || 'YES — I\'M READY TO WAKE UP EASY'} →</button>
            
            <div class="eb-quiz__results-offer-guarantees">
              ${(offer.guarantees || []).map(g => `<span>✓ ${g}</span>`).join('')}
            </div>
          </div>

          <!-- Urgency -->
          <div class="eb-quiz__results-urgency">
            <div class="eb-quiz__results-urgency-icon">⏰</div>
            <p>${offer.urgency || 'This 44% discount is only available on this page.'}</p>
            <p><strong>You've already identified your pattern. You've seen what's causing it. The question is: how many more mornings do you want to lose?</strong></p>
          </div>

          <!-- Final CTA -->
          <div class="eb-quiz__results-final">
            <h2 class="eb-quiz__results-final-headline">${offer.finalHeadline || 'Your Mornings Don\'t Have To Be A Battle'}</h2>
            <p class="eb-quiz__results-final-body">${(offer.finalBody || '').replace(/\n/g, '<br>')}</p>
            <button class="eb-quiz__results-final-cta" data-cta-final>${offer.finalCta || 'START WAKING UP EASY'} →</button>
          </div>

        </div>
      `;
    }

    bindResultsEvents(patternName) {
      const mainCta = this.elements.resultsScreen.querySelector('[data-cta-main]');
      const finalCta = this.elements.resultsScreen.querySelector('[data-cta-final]');

      const handleCta = (ctaType) => {
        QuizAnalytics.trackCTAClick(ctaType, patternName);
        if (this.config.variantId) {
          this.addToCart(this.config.variantId, patternName);
        } else if (this.config.productHandle) {
          window.location.href = 'https://clubearlybird.com/pages/new-dynamic-golife';
        }
      };

      if (mainCta) mainCta.addEventListener('click', () => handleCta('main_cta'));
      if (finalCta) finalCta.addEventListener('click', () => handleCta('final_cta'));
    }

    addToCart(variantId, patternName) {
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: 1,
            properties: {
              '_quiz_pattern': patternName,
              '_quiz_session': QuizAnalytics.sessionId,
              '_quiz_version': '2.0'
            }
          }]
        })
      })
      .then(res => res.json())
      .then(() => window.location.href = '/cart')
      .catch(() => window.location.href = `/products/${this.config.productHandle}`);
    }

    escapeHtml(str) {
      if (!str) return '';
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }
  }


  // ==========================================
  // INITIALIZATION
  // ==========================================
  function initQuiz() {
    document.querySelectorAll('#earlybird-quiz').forEach(container => {
      new EarlyBirdQuizV2(container);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initQuiz);
  } else {
    initQuiz();
  }

  window.EarlyBirdQuizV2 = EarlyBirdQuizV2;
  window.EarlyBirdQuizAnalytics = QuizAnalytics;
})();
